import * as React from "react";
import styled from "@mui/material/styles/styled";
import { InChatDemoReasoningIcon } from "../components/demodebug/mobileSuggestionZoom/inChatDemoReasoningIcon";
import { GenericButton } from "../components/uiControllers/genericButton";
import Screens from "../assets/demo_screens.png";
import { doStartOver } from "../components/startOver/doStartOver";

//region [[ Styles ]]

const DemoLandingView = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "space-evenly",
  flex: 1,
});

export const DemoLayoutTitle = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row",
  width: "100%",
  fontFamily: "Inter",
  fontWeight: 900,
  fontSize: "2.5em",
  color: "#1F0F55",
  [theme.breakpoints.down("xs")]: {
    textAlign: "center",
  },
}));

const DescriptionText = styled("div")(({ theme }) => ({
  fontSize: "14px",
  fontFamily: "Inter",
  fontWeight: 400,
  color: "#1F0F55",
  margin: "10px 0",
  [theme.breakpoints.down("xs")]: {
    textAlign: "center",
  },
}));

const DisclaimerText = styled(DescriptionText)({
  color: "#E84826",
});

const PresentationView = styled("div")({
  display: "flex",
  alignItems: "flex-end",
  flexFlow: "row",
  flexWrap: "wrap-reverse",
  justifyContent: "space-around",
});

const VisualReference = styled("div")({
  flexBasis: "50%",
  "& img": { maxWidth: "100%" },
});

const TryItButton = styled(GenericButton)({
  "&.generic-button": {
    color: "white",
    background: "#5A52C3",
    "&.secondary, &.secondary:hover": {
      color: "#243443",
      background: "#F7F7F9",
      border: "1px solid #5A52C3",
    },
    "&:hover": {
      background: "rgba(90, 82, 195, 0.62)",
    },
    flexBasis: "40%",
    margin: "10px",
  },
});

const ButtonsArea = styled("div")({
  display: "flex",
  flexFlow: "column",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DemoLandingProps {
  onTryItClick: () => void;
  isLoggedIn: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DemoLanding = (props: DemoLandingProps) => {
  const startNewCase = () => {
    doStartOver(null, false);
    props.onTryItClick();
  };

  // If user opened a new tab session storage will reset. if it's ful, we have a case
  const hasOlderCase = !!sessionStorage["temp:conversation"];

  return (
    <DemoLandingView>
      <DemoLayoutTitle>
        Welcome to the Kahun Clinical Assessment Demo
      </DemoLayoutTitle>
      <DescriptionText>
        <p>
          Here you can try Kahun's clinical assessment technology in Demo Mode
          and receive additional clinical insights as you use the product.{" "}
        </p>
        <p>
          Start a session as a patient, describe your symptoms and see how Kahun
          guides the patient through a systematic, evidence-based clinical
          assessment.
        </p>
        <p>
          Wherever you see the <InChatDemoReasoningIcon noBadge /> icon, click
          to see the underlying reasoning for the question, the diseases under
          consideration, and to access the supporting evidence and published
          references that are the source of Kahun's decisions.
        </p>
      </DescriptionText>
      <DisclaimerText>
        NOTE: This information is only visible in DEMO mode, this is not a part
        of the patient facing product
      </DisclaimerText>
      <PresentationView>
        <VisualReference>
          <img
            alt="This information is only visible in DEMO mode, this is not a part
        of the patient facing product"
            src={Screens}
          />
        </VisualReference>
        <ButtonsArea>
          <TryItButton
            onClick={props.onTryItClick}
            label={
              props.isLoggedIn && hasOlderCase
                ? "Continue where you have left off"
                : "Try it now"
            }
          />
          {hasOlderCase && props.isLoggedIn && (
            <TryItButton
              className={"secondary"}
              onClick={startNewCase}
              label={"Start a new case"}
            />
          )}
        </ButtonsArea>
      </PresentationView>
    </DemoLandingView>
  );
};
